import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, TrendingUp, AlertCircle, ChevronRight, X } from "lucide-react";
import { api } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import StatusBadge from "../../components/StatusBadge";

function DepositStat({ icon: Icon, label, value, color }) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-600",
    indigo: "bg-[#FFF4ED] text-[#F97316]",
    red: "bg-red-50 text-red-600",
  };
  return (
    <div className="card-base p-6">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
      <div className="mt-3 text-3xl font-bold font-display">{value}</div>
    </div>
  );
}

function DepositDetailPanel({ deposit, onClose }) {
  if (!deposit) return null;

  const isEscrow = deposit.agreement?.is_escrow;
  const agreement = deposit.agreement;

  return (
    <div
      style={{
        position: "fixed",
        right: 0,
        top: 56,
        bottom: 0,
        width: 420,
        background: "var(--dash-card)",
        borderLeft: "1px solid var(--dash-border)",
        overflowY: "auto",
        zIndex: 40,
        boxShadow: "-4px 0 16px rgba(0,0,0,0.1)",
      }}
    >
      {/* Header */}
      <div style={{ padding: "20px", borderBottom: "1px solid var(--dash-border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: "var(--dash-text)" }}>Deposit Details</h3>
        <button
          onClick={onClose}
          style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--dash-text-2)", padding: 4 }}
        >
          <X style={{ height: 20, width: 20 }} />
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: "20px" }}>
        {/* Deposit Amount */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, color: "var(--dash-text-2)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>
            Deposit Amount
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: "var(--dash-text)", fontFamily: "monospace" }}>
            {formatINR(deposit.deposit_amount)}
          </div>
        </div>

        {/* Agreement Type */}
        <div style={{ marginBottom: 20, padding: "16px", background: "var(--dash-hover)", borderRadius: 12 }}>
          <div style={{ fontSize: 12, color: "var(--dash-text-2)", marginBottom: 4 }}>Agreement Type</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: "var(--dash-text)" }}>
            {isEscrow ? "Deposit Held with Orello" : "Deposit Not Held with Orello"}
          </div>
        </div>

        {/* If Escrow: Show Status & Key Details */}
        {isEscrow && (
          <>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: "var(--dash-text-2)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                Status
              </div>
              <StatusBadge status={deposit.status} />
            </div>

            <div style={{ marginBottom: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: "var(--dash-text-2)", marginBottom: 4 }}>Move-in Date</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--dash-text)" }}>
                  {deposit.lease_start_date ? formatDate(deposit.lease_start_date) : "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: "var(--dash-text-2)", marginBottom: 4 }}>Lease End</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--dash-text)" }}>
                  {deposit.lease_end_date ? formatDate(deposit.lease_end_date) : "—"}
                </div>
              </div>
            </div>

            {/* Tenant Info */}
            <div style={{ marginBottom: 20, padding: "16px", background: "var(--dash-hover)", borderRadius: 12 }}>
              <div style={{ fontSize: 12, color: "var(--dash-text-2)", marginBottom: 8 }}>Tenant</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: "var(--dash-text)" }}>
                {deposit.tenants_resolved?.[0]?.user?.name || "—"}
              </div>
              {deposit.tenants_resolved?.[0]?.user?.phone && (
                <div style={{ fontSize: 12, color: "var(--dash-text-2)", marginTop: 4 }}>
                  {deposit.tenants_resolved?.[0]?.user?.phone}
                </div>
              )}
            </div>

            {/* View Document Button */}
            <Link
              to={`/app/rental/${deposit.id}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "12px 16px",
                background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
                color: "#fff",
                textDecoration: "none",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                width: "100%",
              }}
            >
              View Full Details
              <ChevronRight style={{ height: 16, width: 16 }} />
            </Link>
          </>
        )}

        {/* If NOT Escrow: Show Agreement Type Only */}
        {!isEscrow && (
          <div style={{ padding: "16px", background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.2)", borderRadius: 12, textAlign: "center" }}>
            <Shield style={{ height: 40, width: 40, color: "#F97316", margin: "0 auto 12px" }} />
            <div style={{ fontSize: 13, color: "var(--dash-text)", lineHeight: 1.6 }}>
              Deposit is managed outside Orello. Only agreement details are stored here.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RecentActivityFeed({ deposits }) {
  const activities = deposits
    .slice(0, 5)
    .map((d) => ({
      id: d.id,
      title: d.property_address,
      event: `Status: ${d.status}`,
      date: d.updated_at || d.created_at,
    }));

  return (
    <div className="card-base p-6">
      <h3 className="font-semibold mb-4">Recent Activity</h3>
      <div className="space-y-4">
        {activities.length === 0 ? (
          <p className="text-sm text-gray-500">No recent activity</p>
        ) : (
          activities.map((activity) => (
            <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
              <div className="h-2 w-2 bg-[#F97316] rounded-full mt-2 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{activity.event}</p>
                <p className="text-[10px] text-gray-400 mt-1">
                  {new Date(activity.date).toLocaleDateString("en-IN")}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function LandlordDeposits() {
  const { user } = useAuth();
  const [rentals, setRentals] = useState([]);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rentals");
        setRentals(data);
        if (data.length > 0) setSelectedDeposit(data[0]);
      } catch (err) {
        console.error("Error loading deposits:", err);
      }
    })();
  }, []);

  // Treat rentals as deposits
  const deposits = rentals;
  const active = deposits.filter((d) => d.status === "active").length;
  const held = deposits.reduce((sum, d) => (d.agreement?.is_escrow ? sum + d.deposit_amount : sum), 0);
  const disputes = deposits.filter((d) => d.status === "dispute_review" || d.status === "external_dispute").length;

  const filteredDeposits = (() => {
    switch (filter) {
      case "active":
        return deposits.filter((d) => d.status === "active");
      case "dispute":
        return deposits.filter((d) => d.status === "dispute_review" || d.status === "external_dispute");
      default:
        return deposits;
    }
  })();

  return (
    <div className="space-y-6" data-testid="landlord-deposits">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-display">Deposits</h1>
        <p className="text-sm text-gray-500 mt-1">Manage and track all tenant deposits.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DepositStat icon={TrendingUp} label="Deposits Active" value={active} color="emerald" />
        <DepositStat icon={Shield} label="Held in Escrow" value={formatINR(held)} color="indigo" />
        <DepositStat icon={AlertCircle} label="Ongoing Disputes" value={disputes} color="red" />
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {["all", "active", "dispute"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === f
                ? "bg-[#F97316] text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {f === "all" ? "All Deposits" : f === "active" ? "Active" : "Disputes"}
          </button>
        ))}
      </div>

      {/* Table + Detail Panel */}
      <div style={{ display: "flex", gap: 24, position: "relative" }}>
        {/* Deposits Table */}
        <div className="card-base flex-1" style={{ marginRight: selectedDeposit ? 420 : 0, transition: "margin-right 0.3s ease" }}>
          {filteredDeposits.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p>No deposits found.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase tracking-wider bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-3">Property</th>
                  <th className="text-left px-6 py-3">Tenant</th>
                  <th className="text-right px-6 py-3">Amount</th>
                  <th className="text-left px-6 py-3">Status</th>
                  <th className="text-left px-6 py-3">Agreement</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeposits.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => setSelectedDeposit(d)}
                    className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                      selectedDeposit?.id === d.id ? "bg-orange-50/50" : ""
                    }`}
                  >
                    <td className="px-6 py-4 font-medium">{d.property_address}</td>
                    <td className="px-6 py-4 text-gray-600">{d.tenants_resolved?.[0]?.user?.name || "—"}</td>
                    <td className="px-6 py-4 text-right font-medium">{formatINR(d.deposit_amount)}</td>
                    <td className="px-6 py-4">
                      <StatusBadge status={d.status} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {d.agreement?.is_escrow ? "Escrow" : "Non-Escrow"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail Panel */}
        <DepositDetailPanel deposit={selectedDeposit} onClose={() => setSelectedDeposit(null)} />
      </div>

      {/* Recent Activity */}
      <RecentActivityFeed deposits={deposits} />
    </div>
  );
}
